import {
  ChangeDetectionStrategy,
  Component,
  effect,
  ElementRef,
  HostListener,
  inject,
  OnInit,
  viewChild,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { Router, RouterModule } from '@angular/router';
import { DotLottieWorker } from '@lottiefiles/dotlottie-web';
import { Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import { environment } from '../../environments/environment';
import { AuthService } from '../_generated/api/auth.service';
import { ButtonDirective } from '../ui-components/molecules/button.directive';
import { AuthActions } from './store/auth.actions';
import { authFeature, AuthState } from './store/auth.reducer';

@Component({
  imports: [
    TranslateModule,
    RouterModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    ButtonDirective,
  ],
  template: `
    <div class="relative w-screen h-screen overflow-hidden" tabindex="0">
      <!-- Main login content -->
      <div class="absolute inset-0 flex">
        <!-- Left Div -->
        <div
          class="flex flex-col items-center justify-between flex-1 bg-transparent p-40"
        >
          <!-- Spacer for top -->
          <div></div>

          <!-- Main Content - Centered -->
          <div class="flex flex-col items-center">
            <div class="text-center logo-text-gradient overflow-hidden">
              Compliance <span class="italic">Edge</span
              ><span class="trademark">TM</span>
            </div>
            <p
              class="text-center font-main-text font-normal text-primary-800 pt-16"
              style="font-size: 16px;"
            >
              Your essential compliance resource
            </p>

            <!-- Email Input -->
            <div class="w-[400px] pt-24" [formGroup]="loginForm">
              <input
                type="email"
                formControlName="email"
                placeholder="Enter your email"
                class="w-full px-16 py-12 border border-neutral-600 rounded-[8px] bg-white font-main-text text-p3 text-paragraphs placeholder:text-neutral-400 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
              />
            </div>

            <!-- Password Input -->
            <div class="w-[400px] pt-8" [formGroup]="loginForm">
              <input
                type="password"
                formControlName="password"
                placeholder="Password"
                class="w-full px-16 py-12 border border-neutral-600 rounded-[8px] bg-white font-main-text text-p3 text-paragraphs placeholder:text-neutral-400 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
              />
            </div>

            <!-- Error Message -->
            @if ((error() && hasAttemptedLogin) || errorMessage) {
              <div class="w-[400px] pt-8">
                <div
                  class="w-full px-16 py-12 rounded-[8px] bg-red-50 border border-red-200 text-red-700 text-p3 font-main-text"
                >
                  Wrong identifiers
                </div>
              </div>
            }

            <!-- Continue Button -->
            <div class="w-[400px] pt-16 pb-16">
              <button
                type="button"
                dmcomButton
                variant="filled"
                color="primary"
                size="lg"
                (click)="onSubmit()"
                class="w-full rounded-[8px]"
              >
                Continue
              </button>
            </div>

            <!-- Continue without account Button -->
            <div class="w-[400px]">
              <button
                class="w-full px-16 py-12 bg-transparent font-main-text text-p3 text-grayscale-gray-darker hover:text-primary-500 focus:outline-none transition-colors"
              >
                Continue without an account
              </button>
            </div>
          </div>

          <!-- Powered by DocMagic - Bottom -->
          <div
            class="flex items-center justify-center gap-8 text-xs text-grayscale-gray-darker"
          >
            <span>Powered by</span>
            <img
              src="/assets/logos/logo-docmagic-long.svg"
              class="w-123 h-24"
            />
          </div>
        </div>

        <!-- Right Div -->
        <div
          class="flex-1 mr-20 mt-20 mb-20 bg-white rounded-2xl flex items-center justify-center"
          style="box-shadow: 0 1px 4px 1px rgba(0, 0, 0, 0.08);"
        >
          <canvas
            #lottieCanvas
            class="w-4/5 aspect-[5/6] max-w-[540px] max-h-[652px]"
          ></canvas>
        </div>
      </div>
    </div>
  `,
  styles: `
    .logo-text-gradient {
      font-size: 32px;
      letter-spacing: -0.01em;
      line-height: 120%;
      background: linear-gradient(89.03deg, #1f0e4a 32%, #4e22ba);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LoginPage implements OnInit {
  private readonly authStore = inject<Store<AuthState>>(Store);
  private readonly user = this.authStore.selectSignal(authFeature.selectUser);
  public readonly error = this.authStore.selectSignal(authFeature.selectError);
  public readonly isLoadingFromStore = this.authStore.selectSignal(
    authFeature.selectIsLoading,
  );
  private readonly router = inject(Router);
  private lottieWorker: DotLottieWorker | null = null;
  public readonly lottieCanvas =
    viewChild<ElementRef<HTMLCanvasElement>>('lottieCanvas');
  private readonly fb = inject(FormBuilder);
  private readonly authService = inject(AuthService);

  loginForm: FormGroup;
  hidePassword = true;
  isLoading = false;
  errorMessage = '';
  hasAttemptedLogin = false;

  constructor() {
    this.loginForm = this.fb.group({
      // eslint-disable-next-line @typescript-eslint/unbound-method
      email: ['', [Validators.required, Validators.email]],
      // eslint-disable-next-line @typescript-eslint/unbound-method
      password: ['', [Validators.required, Validators.minLength(6)]],
    });

    effect(() => {
      if (this.user()) {
        void this.router.navigate(['/request']);
      }
    });

    // Clear error when user modifies the form
    this.loginForm.valueChanges.subscribe(() => {
      if (this.error()) {
        // Clear the error by dispatching an action that resets error to null
        this.authStore.dispatch(AuthActions.refreshTokensSuccess());
      }
      // Also clear local validation error
      if (this.errorMessage) {
        this.errorMessage = '';
      }
      // Reset hasAttemptedLogin to false to hide errors
      this.hasAttemptedLogin = false;
    });

    // Manage loading state
    effect(() => {
      if (!this.isLoadingFromStore()) {
        this.isLoading = false;
      }
    });

    // Debug: monitor errors
    effect(() => {
      this.error();
      // Error signal monitoring (console.log removed for production)
    });

    effect(() => {
      const canvas = this.lottieCanvas();
      if (canvas) {
        this.initializeLottieAnimation(canvas.nativeElement);
        return () => {
          this.cleanupLottieAnimation();
        };
      }
      return undefined;
    });
  }

  ngOnInit(): void {
    this.authStore.dispatch(AuthActions.authenticateUser());
  }

  private initializeLottieAnimation(canvas: HTMLCanvasElement): void {
    if (this.lottieWorker) {
      this.cleanupLottieAnimation();
    }

    try {
      this.lottieWorker = new DotLottieWorker({
        canvas,
        src: `${environment.appUiUrl}/assets/lottie/Response1.lottie`,
        loop: true,
        autoplay: true,
      });
    } catch (error) {
      console.error('Failed to initialize Lottie animation:', error);
    }
  }

  private cleanupLottieAnimation(): void {
    if (this.lottieWorker) {
      void this.lottieWorker.destroy();
      this.lottieWorker = null;
    }
  }

  onSubmit() {
    this.hasAttemptedLogin = true; // Mark that an attempt has been made

    if (this.loginForm.valid && !this.isLoading) {
      this.isLoading = true;
      this.errorMessage = '';

      const formValue = this.loginForm.value as {
        email: string;
        password: string;
      };
      const { email, password } = formValue;

      this.authStore.dispatch(AuthActions.login({ email, password }));
    } else if (!this.loginForm.valid) {
      // Display the same error message for validation errors
      this.errorMessage = 'Wrong identifiers';
    }
  }

  @HostListener('keydown.enter', ['$event'])
  onEnterKey(event: KeyboardEvent) {
    // Prevent default behavior to avoid form submission
    event.preventDefault();
    this.onSubmit();
  }
}
